import React, { useCallback, useState } from 'react';
import { Button, ButtonGroup, Input, InputGroup, InputGroupAddon, InputGroupText } from 'reactstrap';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { ChecklistEditorContext, ChecklistEditorDispatchContext } from './checklist-editor-context';
import { useAutosaveChanges } from './utils';
import { Actions } from './checklist-editor-reducer';
import { QuestionTypeDefinition } from './questions';
import { SortIcon } from './questions/icons';
import Icon from '../components/Icon';
import { DeleteButton } from './DeleteButton';
import { useContextSelector } from 'use-context-selector';

export function EditableQuestion({
    groupId,
    questionId,
    questionIndex,
}) {
    const {
        setNodeRef,
        setActivatorNodeRef,
        listeners,
        attributes,
        transform,
        transition,
    } = useSortable({
        id: questionId,
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
    };

    const dispatch = useContextSelector(ChecklistEditorDispatchContext, state => state);
    const question = useContextSelector(ChecklistEditorContext, state => state.questionsById[questionId]);

    const {
        icon: QuestionTypeIcon,
        component: QuestionComponent,
        props: questionComponentProps,
    } = QuestionTypeDefinition[question.type];

    const [ isDirty, setIsDirty ] = useState(false);
    const [ name, setName ] = useState(() => question.name);

    const saveUncommittedChanges = useCallback(() => {
        if (!isDirty) {
            return;
        }

        const nextName = name.trim();
        if (nextName !== name) {
            setName(nextName);
        }

        dispatch({
            type: Actions.UPDATE_QUESTION,
            payload: {
                questionId,
                name: nextName,
            },
        });

        setIsDirty(false);
    }, [ isDirty, dispatch, name, questionId ]);

    useAutosaveChanges(isDirty, saveUncommittedChanges);

    const renameQuestion = (e) => {
        const value = e.target?.value ?? '';
        if (name === value) {
            return;
        }

        setName(value);
        setIsDirty(true);
    };

    const moveQuestionUp = () => dispatch({
        type: Actions.MOVE_QUESTION,
        payload: {
            groupId: groupId,
            from: questionIndex,
            to: questionIndex - 1,
        },
    });

    const moveQuestionDown = () => dispatch({
        type: Actions.MOVE_QUESTION,
        payload: {
            groupId: groupId,
            from: questionIndex,
            to: questionIndex + 1,
        },
    });

    const deleteQuestion = () => dispatch({
        type: Actions.DELETE_QUESTION,
        payload: {
            groupId: groupId,
            questionId: questionId,
        },
    });

    return (<div
        className={ 'checklist-editor__panel checklist-editor__panel--question' }
        ref={ setNodeRef }
        style={ style }
    >
        <div
            className={ 'checklist-editor__sort-handle' }
            ref={ setActivatorNodeRef }
            { ...attributes }
            { ...listeners }
        >
            <SortIcon />
        </div>

        <div className={ 'checklist-editor__panel-head' }>
            <div className={ 'checklist-editor__panel-heading' }>
                <InputGroup>
                    <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                            <QuestionTypeIcon />
                        </InputGroupText>
                    </InputGroupAddon>
                    <Input
                        type={ 'text' }
                        value={ name }
                        placeholder={ 'Enter question name' }
                        onChange={ renameQuestion }
                        onBlur={ saveUncommittedChanges }
                    />
                </InputGroup>
            </div>
            <div className={ 'checklist-editor__actions' }>
                <ButtonGroup>
                    <Button
                        outline
                        size={ 'sm' }
                        onClick={ moveQuestionUp }
                    >
                        <Icon icon="arrow-up" />
                    </Button>
                    <Button
                        outline
                        size={ 'sm' }
                        onClick={ moveQuestionDown }
                    >
                        <Icon icon="arrow-down" />
                    </Button>
                </ButtonGroup>
                <DeleteButton onConfirm={ deleteQuestion } />
            </div>
        </div>

        <div className={ `checklist-editor__panel-body` }>
            <QuestionComponent { ...questionComponentProps } questionId={ questionId } />
        </div>

        <div className={ 'checklist-editor__panel-foot' }>
        </div>
    </div>);
}
